import {
  inferOrderProductExtraMetadata,
  orderProductMatchesCartLine,
  type ProductWithExtras,
} from "./webstoreCartExtras";
import type { CheckoutOrderProduct } from "./checkoutTypes";
import type { CartLineIdentity } from "./cartIdentity";

export type CartItemLike = {
  uuid?: string;
  productUuid: string;
  quantity: number;
  eventHash?: string;
  start?: string;
  eventStart?: string;
  cartLineSource?: CartLineIdentity["cartLineSource"];
  parentUuid?: string | null;
  parentProductUuids?: string[];
  invalid?: boolean;
  productName?: string;
};

export type OrderProductForDisplay = OrderProductEventFields & {
  uuid?: string;
  productUuid: string;
  quantity: number;
  cartLineSource?: CartLineIdentity["cartLineSource"];
  parentUuid?: string | null;
  parentProductUuids?: string[];
  invalid?: boolean;
  productName?: string;
};

export type CartDisplaySection = {
  key: string;
  label: string | null;
  /** Event date label when present; otherwise an empty separator row for standard products. */
  showGroupRow?: boolean;
  items: CartItemLike[];
  sortStartMs?: number;
};

function parseStartMs(start?: string): number | null {
  if (!start) return null;
  const ms = new Date(start).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function eventStartsMatch(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const aMs = parseStartMs(a);
  const bMs = parseStartMs(b);
  if (aMs !== null && bMs !== null) {
    return aMs === bMs;
  }
  return a === b;
}

function eventGroupKey(item: CartItemLike): string | null {
  const startPart = item.start ?? "no_start";
  if (item.eventHash) return `event_hash_${item.eventHash}_${startPart}`;
  if (item.start) return `event_start_${item.start}`;
  return null;
}

type OrderProductEventFields = {
  eventHash?: string | null;
  eventStart?: string | null;
};

export function getOrderProductEventStart(
  item: OrderProductEventFields
): string | undefined {
  const candidates = [
    item.eventStart,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return undefined;
}

export function orderProductHasEventContext(
  item: OrderProductEventFields
): boolean {
  return !!(
    item.eventHash ||
    getOrderProductEventStart(item)
  );
}

/** Per-line datetime under product name on order-completed checkout (not group headers). */
export function formatOrderLineEventDateTime(
  start: string,
  locale?: string
): string {
  const date = new Date(start);
  if (!Number.isFinite(date.getTime())) return "";
  const loc = locale || "et-EE";
  const weekday = date.toLocaleDateString(loc, { weekday: "short" });
  const datePart = date.toLocaleDateString(loc, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString(loc, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${weekday}, ${datePart}, ${timePart}`;
}

function formatEventGroupLabel(start: string, locale?: string): string {
  const date = new Date(start);
  if (!Number.isFinite(date.getTime())) return "";
  const weekday = date
    .toLocaleDateString(locale || "et-EE", { weekday: "short" })
    .charAt(0)
    .toUpperCase();
  const datePart = date.toLocaleDateString(locale || "et-EE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString(locale || "et-EE", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${weekday} ${datePart} ${timePart}`;
}

function lineRepresentsExtra(item: CartItemLike): boolean {
  return (
    item.cartLineSource === "autoExtra" ||
    (item.parentProductUuids?.length ?? 0) > 0 ||
    !!item.parentUuid
  );
}

function extraBelongsUnderMain(extra: CartItemLike, main: CartItemLike): boolean {
  if (extra.parentUuid && main.uuid && extra.parentUuid === main.uuid) {
    return true;
  }
  if (!extra.parentProductUuids?.length || !main.productUuid) {
    return false;
  }
  return extra.parentProductUuids.includes(main.productUuid);
}

function orderItemsWithExtras(items: CartItemLike[]): CartItemLike[] {
  const mains = items.filter((i) => !lineRepresentsExtra(i));
  const extras = items.filter((i) => lineRepresentsExtra(i));
  const usedExtras = new Set<number>();
  const ordered: CartItemLike[] = [];

  mains.forEach((main) => {
    ordered.push(main);
    extras.forEach((extra, idx) => {
      if (usedExtras.has(idx)) return;
      if (extraBelongsUnderMain(extra, main)) {
        ordered.push(extra);
        usedExtras.add(idx);
      }
    });
  });

  extras.forEach((extra, idx) => {
    if (!usedExtras.has(idx)) {
      ordered.push(extra);
    }
  });

  return ordered;
}

export function buildCartDisplaySections(
  cartItems: CartItemLike[],
  locale?: string
): CartDisplaySection[] {
  const noEventItems: CartItemLike[] = [];
  const byEvent = new Map<string, CartItemLike[]>();
  const eventStartByKey = new Map<string, string>();

  cartItems.forEach((item) => {
    const key = eventGroupKey(item);
    if (!key) {
      noEventItems.push(item);
      return;
    }
    const groupItems = byEvent.get(key) ?? [];
    groupItems.push(item);
    byEvent.set(key, groupItems);
    if (item.start && !eventStartByKey.has(key)) {
      eventStartByKey.set(key, item.start);
    }
  });

  const eventSections: CartDisplaySection[] = [...byEvent.entries()].map(
    ([key, items]) => {
      const start = eventStartByKey.get(key);
      const sortStartMs = parseStartMs(start ?? undefined) ?? undefined;
      return {
        key,
        label: start ? formatEventGroupLabel(start, locale) : null,
        items: orderItemsWithExtras(items),
        sortStartMs,
      };
    }
  );

  eventSections.sort((a, b) => {
    const aMs = a.sortStartMs ?? Number.MAX_SAFE_INTEGER;
    const bMs = b.sortStartMs ?? Number.MAX_SAFE_INTEGER;
    return aMs - bMs;
  });

  const sections: CartDisplaySection[] = [];

  if (noEventItems.length > 0) {
    sections.push({
      key: "no_event",
      label: null,
      showGroupRow: true,
      items: orderItemsWithExtras(noEventItems),
    });
  }

  sections.push(...eventSections);

  return sections;
}

function orderProductLineMatchFields(
  op: Record<string, unknown>,
  parentProductUuidByOrderLineUuid?: Map<string, string>
) {
  const parentUuid = op.parentUuid as string | undefined;
  const parentProductUuids = op.parentProductUuids as string[] | undefined;
  const resolvedParentProductUuid = parentUuid
    ? parentProductUuidByOrderLineUuid?.get(parentUuid)
    : undefined;

  return {
    productUuid: op.productUuid as string | undefined,
    eventHash: op.eventHash as string | undefined,
    eventStart: getOrderProductEventStart(op),
    parentUuid,
    parentProductUuids: resolvedParentProductUuid
      ? [...new Set([...(parentProductUuids ?? []), resolvedParentProductUuid])]
      : parentProductUuids,
  };
}

function normalizeParentUuids(
  parentUuid?: string | null,
  parentProductUuids?: string[]
): string[] | undefined {
  const normalized = [
    ...new Set(
      [...(parentProductUuids ?? []), parentUuid ?? undefined].filter(
        (uuid): uuid is string =>
          typeof uuid === "string" && uuid.trim().length > 0
      )
    ),
  ];
  return normalized.length ? normalized : undefined;
}

function parentLinksAreCompatible(
  item: CartItemLike,
  op: ReturnType<typeof orderProductLineMatchFields>
): boolean {
  const itemParents = normalizeParentUuids(
    item.parentUuid,
    item.parentProductUuids
  );
  const orderParents = normalizeParentUuids(op.parentUuid, op.parentProductUuids);
  if (!itemParents?.length || !orderParents?.length) {
    return true;
  }
  return orderParents.some((parentUuid) => itemParents.includes(parentUuid));
}

function orderProductMatchesCartLineForDisplay(
  item: CartItemLike,
  op: ReturnType<typeof orderProductLineMatchFields>
): boolean {
  if (!item.productUuid || !op.productUuid || item.productUuid !== op.productUuid) {
    return false;
  }
  if (op.eventStart && item.start && !eventStartsMatch(item.start, op.eventStart)) {
    return false;
  }
  if (orderProductMatchesCartLine(item, op)) {
    return true;
  }
  if (op.eventHash || !item.eventHash) {
    return false;
  }
  if (!eventStartsMatch(item.start, op.eventStart)) {
    return false;
  }
  return parentLinksAreCompatible(item, op);
}

function findUnusedCartMatch(
  cartItems: CartItemLike[] | undefined,
  op: Record<string, unknown>,
  usedCartItemIndexes: Set<number>,
  parentProductUuidByOrderLineUuid?: Map<string, string>
): CartItemLike | undefined {
  if (!cartItems?.length) {
    return undefined;
  }

  const matchFields = orderProductLineMatchFields(
    op,
    parentProductUuidByOrderLineUuid
  );
  const matchIndex = cartItems.findIndex(
    (item, index) =>
      !usedCartItemIndexes.has(index) &&
      orderProductMatchesCartLineForDisplay(item, matchFields)
  );

  if (matchIndex < 0) {
    return undefined;
  }

  usedCartItemIndexes.add(matchIndex);
  return cartItems[matchIndex];
}

/** Ensures confirmed order lines carry event start and extra-line metadata for cart UI. */
export function enrichOrderProductsForConfirmedDisplay<
  T extends Record<string, unknown>,
>(
  orderProducts: T[],
  cartItems?: CartItemLike[],
  products?: ProductWithExtras[]
): T[] {
  const usedCartItemIndexes = new Set<number>();
  const parentProductUuidByOrderLineUuid = new Map<string, string>();
  orderProducts.forEach((op) => {
    const uuid = op.uuid;
    const productUuid = op.productUuid;
    if (typeof uuid === "string" && typeof productUuid === "string") {
      parentProductUuidByOrderLineUuid.set(uuid, productUuid);
    }
  });

  return orderProducts.map((op) => {
    const cartMatch = findUnusedCartMatch(
      cartItems,
      op,
      usedCartItemIndexes,
      parentProductUuidByOrderLineUuid
    );

    const start =
      getOrderProductEventStart(op) ?? cartMatch?.start;

    const inferredExtra =
      !(op.cartLineSource as string | undefined) &&
      !(op.parentProductUuids as string[] | undefined)?.length &&
      products?.length
        ? inferOrderProductExtraMetadata(
            op as unknown as OrderProductForDisplay,
            orderProducts as unknown as OrderProductForDisplay[],
            products
          )
        : undefined;

    const parentUuid = op.parentUuid as string | null | undefined;
    const cartLineSource =
      (op.cartLineSource as CartItemLike["cartLineSource"] | undefined) ??
      cartMatch?.cartLineSource ??
      inferredExtra?.cartLineSource ??
      (parentUuid ? "autoExtra" : undefined);

    const parentProductUuids =
      (op.parentProductUuids as string[] | undefined) ??
      cartMatch?.parentProductUuids ??
      inferredExtra?.parentProductUuids;

    let next: T = { ...op };

    if (start) {
      next = {
        ...next,
        eventStart: start,
      };
    }

    if (cartLineSource || parentProductUuids?.length) {
      next = {
        ...next,
        ...(cartLineSource ? { cartLineSource } : {}),
        ...(parentProductUuids?.length
          ? { parentProductUuids }
          : {}),
      };
    }

    return next;
  });
}

/** Append invalid order lines missing from cart so checkout can show them. */
export function mergeInvalidOrderProductsIntoCartItems<T extends CartItemLike>(
  cartItems: T[],
  orderProducts: Array<
    Pick<
      CheckoutOrderProduct,
      | "productUuid"
      | "quantity"
      | "invalid"
      | "productName"
      | "eventHash"
      | "eventStart"
      | "parentUuid"
      | "parentProductUuids"
      | "cartLineSource"
    >
  >
): T[] {
  const missingInvalid = orderProducts.filter(
    (op) =>
      op.invalid === true &&
      op.productUuid &&
      !cartItems.some((item) => orderProductMatchesCartLine(item, op))
  );
  if (missingInvalid.length === 0) {
    return cartItems;
  }

  const appended = missingInvalid.map((op) => {
    const start = getOrderProductEventStart(op);
    const parentProductUuids = [
      ...new Set(
        [...(op.parentProductUuids ?? []), op.parentUuid ?? undefined].filter(
          (uuid): uuid is string =>
            typeof uuid === "string" && uuid.trim().length > 0
        )
      ),
    ];
    const isExtraLinked =
      op.cartLineSource === "autoExtra" || parentProductUuids.length > 0;
    return {
      productUuid: op.productUuid,
      quantity: op.quantity,
      invalid: true as const,
      productName: op.productName,
      eventHash: op.eventHash ?? undefined,
      start,
      eventStart: start,
      ...(isExtraLinked ? { cartLineSource: "autoExtra" as const } : {}),
      ...(parentProductUuids.length ? { parentProductUuids } : {}),
    } as T;
  });

  return [...cartItems, ...appended];
}

/** Map confirmed order lines to cart line shape for shared checkout/cart UI. */
export function orderProductsToDisplayCartItems(
  orderProducts: OrderProductForDisplay[]
): CartItemLike[] {
  return orderProducts
    .map((op) => {
      const start = getOrderProductEventStart(op);
      const eventHash = op.eventHash ?? undefined;
      const parentUuid = op.parentUuid ?? undefined;
      const cartLineSource =
        op.cartLineSource ?? (parentUuid ? "autoExtra" : undefined);

      return {
        uuid: op.uuid,
        productUuid: op.productUuid,
        quantity: op.quantity,
        eventHash: eventHash ?? undefined,
        eventStart: start,
        start: start,
        cartLineSource,
        parentUuid,
        parentProductUuids: op.parentProductUuids,
        invalid: op.invalid === true,
        productName: op.productName,
      };
    })
    .filter((item) => !!item.productUuid);
}
